import React, { useRef, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';

import DatePicker from 'components/DatePicker';
import TextArea from 'components/TextArea';
import Spinner from 'components/Spinner';
import Select from 'components/Select';

import { interesses_url } from 'config/environment';
import api from 'services/api';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { show } from 'store/modules/dialog/actions';
import { SolicitacaoDigital } from './index';

import { Container, Form, FormGroups } from './styles';
interface IInteresses {
  name: string;
  values: string[];
}

// configuração do validator
const schema = Yup.object().shape({
  nome: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[^<>:"/\\|?*]+$/, {
      message: 'O nome contém caracteres inválidos.',
      excludeEmptyString: true,
    })
    .max(100, 'Limite de 100 caracteres excedido'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Deadline inválido'),
  tarefa: Yup.string().required('Campo Obrigatório'),
  praca: Yup.string().required('Campo Obrigatório'),
  produto: Yup.string().required('Campo Obrigatório'),
  canal: Yup.string().required('Campo Obrigatório'),
  conteudo: Yup.string().required('Campo Obrigatório'),
  pedidoinfo: Yup.string().required('Campo Obrigatório'),
});

export default function SolicitacaoDigitalStore() {
  // react-router-dom
  const navigate = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [interesses, setInteresses] = useState<IInteresses[]>([]);
  const [initialData] = useState({} as SolicitacaoDigital);
  const [clickupWorkspace, setClickupWorkspace] = useState<any>([]);

  /**
   * REDUX
   */
  const dispatch = useDispatch();

  /**
   * USE REF
   */
  const ref = useRef<FormHandles>(null);
  /**
   * USE CALLBACK
   */

  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  // Handle SUBMIT
  const handleSubmit = useCallback(
    async data => {
      try {
        if (ref.current) {
          setLoadingSubmit(true);
          // // Realiza validação de campos obrigatórios
          ref.current.setErrors({});
          if (!data.programa) delete data.programa;
          await schema.validate(data, { abortEarly: false });

          // Envia requisição de criação ou edição para o backend
          const interessesJson = JSON.stringify(
            interesses.map(i => ({
              name: i.name,
              values: data[i.name],
            })),
          );
          interesses.forEach(i => {
            delete data[i.name];
          });
          // Envia requisição de criação ou edição para o backend
          const response = await api.post('/solicitacaodigital', {
            ...data,
            interesses: interessesJson,
            projetonovo: data.projetonovo === 'novo',
          });
          // Redireciona usuário em caso de sucesso
          if (response && response.data)
            handleSuccessMessage({
              contentHTML: `<h3>Solicitação salva!</h3> <p>Você será redirecionado para a lista de solicitações em 3 segundos</p>`,
              redirectTo: `/solicitacaodigital`,
            });
          else toast.error('Falha ao salvar a solicitação');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (ref.current) ref.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleSuccessMessage, interesses],
  );
  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadClickupWorkspace() {
      try {
        const response = await api.get('/clickup/workspace');
        if (response && response.data) {
          setClickupWorkspace(response.data);
        } else toast.error('Falha ao carregar arquivo Json do ClickUp');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    loadClickupWorkspace();
  }, []);

  useEffect(() => {
    async function loadInteresses() {
      try {
        const response = await axios.get(interesses_url);
        if (response && response.data && response.data.interests)
          setInteresses(response.data.interests);
        else toast.error('Falha ao carregar Interesses');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    loadInteresses();
  }, []);

  const produtoOptions =
    clickupWorkspace?.spaces?.[0]?.custom_fields
      ?.find(c => c.name === 'Produto')
      ?.options?.map(o => ({
        value: o.name,
        label: o.name,
      })) ?? [];
  const tarefaOptions =
    clickupWorkspace?.spaces?.[0]?.custom_fields
      ?.find(c => c.name === 'Tarefa')
      ?.options?.map(o => ({
        value: o.name,
        label: o.name,
      })) ?? [];
  const pracaOptions =
    clickupWorkspace?.spaces?.[0]?.custom_fields
      ?.find(c => c.name === 'Praca')
      ?.options?.map(o => ({
        value: o.name,
        label: o.name,
      })) ?? [];
  const canalOptions =
    clickupWorkspace?.spaces?.[0]?.custom_fields
      ?.find(c => c.name === 'Canal')
      ?.optionsoptions?.map(o => ({
        value: o.name,
        label: o.name,
      })) ?? [];

  const handleSelectData = useCallback(() => {
    try {
      const optionBase = {
        type: 'warning',
        text: `Para que a demanda entre no fluxo, ela deve ser solicitada com no mínimo 3 dias úteis de antecedência.
              Em casos de projetos de grande porte, será necessária uma análise da equipe para definição do prazo de entrega`,
        confirmButtonText: 'CONFIRMAR',
        haveCallback: false,
        cancelButtonText: 'OK',
        showCancelButton: true,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(optionBase));
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        const appError = error as AppError;
        toast.error(appError.message);
      }
    }
  }, [dispatch]);

  return (
    <Container>
      <Card>
        <Form ref={ref} onSubmit={handleSubmit} initialData={initialData}>
          <div>
            <header>
              <h4>SOLICITAÇÕES - DIGITAL</h4>
            </header>
            <FormGroups $columns="4fr 2fr">
              <div>
                <section>
                  <h5>Nome da Solicitação (o título que vamos divulgar) *</h5>
                </section>
                <Input
                  name="nome"
                  max={100}
                  placeholder="Nome do Solicitação *"
                />
              </div>
              <div>
                <section>
                  <h5>Qual o deadline para colocarmos a ação no ar? *</h5>
                </section>
                <DatePicker
                  onSelect={() => handleSelectData()}
                  placeholderText="Deadline"
                  minDate={new Date()}
                  name="datafim"
                  disabled={false}
                />
              </div>
            </FormGroups>
            <FormGroups $columns="1fr 1fr 1fr 1fr">
              <div>
                <section>
                  <h5>Produto *</h5>
                </section>
                <Select
                  name="produto"
                  placeholder="Produto"
                  options={produtoOptions}
                  isSearchable
                  fill={false}
                />
              </div>
              <div>
                <section>
                  <h5>Qual a Praça? *</h5>
                </section>
                <Select
                  name="praca"
                  placeholder="Praca"
                  options={pracaOptions}
                  isSearchable
                  fill={false}
                />
              </div>
              <div>
                <section>
                  <h5>Qual a Tarefa? *</h5>
                </section>
                <Select
                  name="tarefa"
                  placeholder="Tarefa"
                  options={tarefaOptions}
                  isSearchable
                  fill={false}
                />
              </div>
              <div>
                <section>
                  <h5>Qual a Canal? *</h5>
                </section>
                <Select
                  name="canal"
                  placeholder="Canal"
                  options={canalOptions}
                  isSearchable
                  fill={false}
                />
              </div>
            </FormGroups>
            <div>
              <section>
                <h5>Explica pra gente o seu pedido? *</h5>
              </section>
              <TextArea
                id="pedidoinfo"
                name="pedidoinfo"
                height="70"
                placeholder="Queremos saber o contexto da ação, os horários que precisamos subir os conteúdos, se temos outras áreas envolvidas... *"
                icon=""
                errorField=""
              />
            </div>
            <div>
              <section>
                <h5>Você já tem o texto ou conteúdo pronto? *</h5>
              </section>
              <TextArea
                id="conteudo"
                name="conteudo"
                height="70"
                placeholder="Cola aqui pra gente! *"
                icon=""
                errorField=""
              />
            </div>
            <section>
              <h5>Tem algum link ou caminho de pastas?</h5>
            </section>
            <div>
              <Input
                name="outroslinks"
                placeholder="Coloque aqui a pasta com o KV e os materiais necessários"
              />
            </div>
            <section>
              <h5>Interesses</h5>
            </section>
            <FormGroups $columns="1fr 1fr 1fr 1fr">
              {interesses &&
                interesses.map((interesse, i) => {
                  return (
                    <div
                      key={`interesse${i}`}
                      style={{ margin: '0 0.3rem 0.6rem' }}
                    >
                      <Select
                        name={interesse.name}
                        placeholder={interesse.name}
                        options={interesse.values.map(v => ({
                          value: v,
                          label: v,
                        }))}
                        isSearchable
                        isMulti
                        fill={false}
                      />
                    </div>
                  );
                })}
            </FormGroups>
          </div>

          <div className="ButtonGroup">
            <Button
              color="primary"
              onClick={e => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              <FiArrowLeft />
              VOLTAR
            </Button>
            <Button className="submitButton" type="submit" color="primary">
              <FiSave />
              {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
